import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Building2,
  MapPin,
  Mail,
  Phone,
  Calendar,
  FileText,
  Globe,
  Users,
  Briefcase,
  Award,
  ExternalLink,
  Loader
} from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import { authService } from '../../services/authService'
import { companyService } from '../../services/companyService'
import toast from 'react-hot-toast'
const CompanyProfileView = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user?.id && !user?.user_id) {
        setError('User ID not found')
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        const userId = user.id || user.user_id
        console.log('Fetching profile for userId:', userId)
        const response = await companyService.getCompanyProfile(userId)
        console.log('API Response:', response)

        if (response.success && response.data) {
          // Extract nested data structure
          const {
            user_details = {},
            company_details = {},
            company_profile_details = {},
            company_subscription_details = {}
          } = response.data

          // Combine all data into a single profile object
          const profileData = {
            // User details
            user_id: user_details.id,
            user_name: user_details.name,
            user_email: user_details.email,
            user_created_at: user_details.created_at,

            // Company details
            company_name: company_details.company_name,
            legal_entity_name: company_details.legal_entity_name,
            corporate_email: company_details.corporate_email || company_details.email,
            phone_number: company_details.phone_number,
            pan_tin: company_details.pan_tin,
            gstin: company_details.gstin,
            address_line_1: company_details.address_line_1,
            address_line_2: company_details.address_line_2,
            city: company_details.city,
            state: company_details.state,
            postal_code: company_details.postal_code,
            country: company_details.country,
            status: company_details.status,
            BGV_status: company_details.bgv_status,
            admin_review: company_details.admin_review,
            created_at: company_details.created_at,
            updated_at: company_details.updated_at,
            business_domain: company_details.business_domain,
            company_size: company_details.company_size,
            company_description: company_details.company_description,
            website_url: company_details.website_url,
            full_name: company_details.full_name,

            // Company profile details
            company_profile: {
              industry_domains: company_profile_details.industry_domains,
              key_technologies: company_profile_details.key_technologies,
              detailed_description: company_profile_details.detailed_description,
              years_in_operation: company_profile_details.years_in_operation,
              linkedin_url: company_profile_details.linkedin_url,
              brand_assets_url: company_profile_details.brand_assets_url,
              logo_url: company_profile_details.logo_url,
              other_social_handles: company_profile_details.other_social_handles
            },

            // Subscription details
            subscription: {
              plan_name: company_subscription_details.plan_name,
              amount: company_subscription_details.amount,
              billing_cycle: company_subscription_details.billing_cycle,
              payment_status: company_subscription_details.payment_status,
              payment_method: company_subscription_details.payment_method,
              final_amount: company_subscription_details.final_amount
            },

            // Documents (if available in response)
            all_documents_urls: response.data.all_documents_urls || {}
          }

          console.log('Processed profile data:', profileData)
          setProfile(profileData)
        } else {
          setError('Failed to fetch company profile')
        }
      } catch (err) {
        console.error('Error fetching company profile:', err)
        setError(err.message || 'Failed to load company profile')
        toast.error('Failed to load company profile')
      } finally {
        setLoading(false)
      }
    }

    fetchProfile()
  }, [user])

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-accent-50 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-16 h-16 mx-auto mb-4 animate-spin text-primary-600" />
          <p className="text-lg font-semibold text-slate-900 dark:text-white">Loading company profile...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-accent-50 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-xl mb-4">Error loading company profile</div>
          <p className="text-slate-600 dark:text-gray-400 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-accent-50 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-600 dark:text-gray-400">No profile data available</p>
        </div>
      </div>
    )
  }

  // Extract profile data
  const {
    company_name,
    legal_entity_name,
    corporate_email,
    phone_number,
    pan_tin,
    gstin,
    address_line_1,
    address_line_2,
    city,
    state,
    postal_code,
    country,
    created_at,
    updated_at,
    company_profile,
    all_documents_urls,
    subscription,
    status,
    BGV_status,
    admin_review,
    business_domain,
    company_size,
    company_description,
    website_url,
    full_name,
    user_name,
    user_email
  } = profile

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-accent-50 dark:from-slate-900 dark:to-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >


          <div className="card p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-gradient-to-r from-primary-600 to-accent-600 rounded-xl flex items-center justify-center">
                  <span className="text-white text-2xl font-bold">
                    {(company_name || legal_entity_name || 'C').charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-slate-900 dark:text-white">
                    {company_name || legal_entity_name || 'Company Profile'}
                  </h1>
                  {legal_entity_name && legal_entity_name !== company_name && (
                    <p className="text-slate-600 dark:text-gray-400 mt-1">{legal_entity_name}</p>
                  )}
                  <div className="flex items-center space-x-4 mt-2">
                    {status && (
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                        status === 'pending' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                          'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                        }`}>
                        {status.toUpperCase()}
                      </span>
                    )}
                    {BGV_status && (
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${BGV_status === 'completed' || BGV_status === 'approved' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                        BGV_status === 'rejected' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                          'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                        }`}>
                        BGV: {BGV_status.toUpperCase()}
                      </span>
                    )}
                    {admin_review && (
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${admin_review === 'approved' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                        admin_review === 'rejected' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                          'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                        }`}>
                        Admin: {admin_review.toUpperCase()}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Company Information */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="card p-6"
            >
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-6 flex items-center space-x-2">
                <Building2 className="w-6 h-6" />
                <span>Company Information</span>
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm font-medium text-slate-500 dark:text-gray-400">Company Name</label>
                  <p className="mt-1 text-slate-900 dark:text-white">{company_name || '-'}</p>
                </div>

                {legal_entity_name && (
                  <div>
                    <label className="text-sm font-medium text-slate-500 dark:text-gray-400">Legal Entity Name</label>
                    <p className="mt-1 text-slate-900 dark:text-white">{legal_entity_name}</p>
                  </div>
                )}

                <div>
                  <label className="text-sm font-medium text-slate-500 dark:text-gray-400">PAN/TIN</label>
                  <p className="mt-1 text-slate-900 dark:text-white">{pan_tin || '-'}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-500 dark:text-gray-400">GSTIN</label>
                  <p className="mt-1 text-slate-900 dark:text-white">{gstin || '-'}</p>
                </div>

                {business_domain && (
                  <div>
                    <label className="text-sm font-medium text-slate-500 dark:text-gray-400">Business Domain</label>
                    <p className="mt-1 text-slate-900 dark:text-white">{business_domain}</p>
                  </div>
                )}

                {company_size && (
                  <div>
                    <label className="text-sm font-medium text-slate-500 dark:text-gray-400">Company Size</label>
                    <p className="mt-1 text-slate-900 dark:text-white">{company_size}</p>
                  </div>
                )}

                <div>
                  <label className="text-sm font-medium text-slate-500 dark:text-gray-400 flex items-center space-x-1">
                    <Mail className="w-4 h-4" />
                    <span>Corporate Email</span>
                  </label>
                  <p className="mt-1 text-slate-900 dark:text-white">{corporate_email || '-'}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-500 dark:text-gray-400 flex items-center space-x-1">
                    <Phone className="w-4 h-4" />
                    <span>Phone Number</span>
                  </label>
                  <p className="mt-1 text-slate-900 dark:text-white">{phone_number || '-'}</p>
                </div>

                {full_name && (
                  <div>
                    <label className="text-sm font-medium text-slate-500 dark:text-gray-400">Contact Person</label>
                    <p className="mt-1 text-slate-900 dark:text-white">{full_name}</p>
                  </div>
                )}

                {website_url && (
                  <div>
                    <label className="text-sm font-medium text-slate-500 dark:text-gray-400 flex items-center space-x-1">
                      <Globe className="w-4 h-4" />
                      <span>Website</span>
                    </label>
                    <a
                      href={website_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-1 text-primary-600 dark:text-blue-400 hover:underline flex items-center space-x-1"
                    >
                      <span>{website_url}</span>
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                )}

                {created_at && (
                  <div>
                    <label className="text-sm font-medium text-slate-500 dark:text-gray-400 flex items-center space-x-1">
                      <Calendar className="w-4 h-4" />
                      <span>Registration Date</span>
                    </label>
                    <p className="mt-1 text-slate-900 dark:text-white">
                      {new Date(created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                )}

                {company_description && (
                  <div className="md:col-span-2">
                    <label className="text-sm font-medium text-slate-500 dark:text-gray-400">Company Description</label>
                    <p className="mt-1 text-slate-900 dark:text-white">{company_description}</p>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Address Information */}
            {(address_line_1 || city || state) && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="card p-6"
              >
                <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-6 flex items-center space-x-2">
                  <MapPin className="w-6 h-6" />
                  <span>Address</span>
                </h2>

                <div className="space-y-2">
                  {address_line_1 && <p className="text-slate-900 dark:text-white">{address_line_1}</p>}
                  {address_line_2 && <p className="text-slate-900 dark:text-white">{address_line_2}</p>}
                  <p className="text-slate-900 dark:text-white">
                    {[city, state, postal_code].filter(Boolean).join(', ')}
                  </p>
                  {country && <p className="text-slate-900 dark:text-white">{country}</p>}
                </div>
              </motion.div>
            )}

            {/* Documents */}
            {all_documents_urls && Object.keys(all_documents_urls).length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="card p-6"
              >
                <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-6 flex items-center space-x-2">
                  <FileText className="w-6 h-6" />
                  <span>Documents</span>
                </h2>

                <div className="space-y-3">
                  {Object.entries(all_documents_urls).map(([docKey, docData]) => (
                    <div
                      key={docKey}
                      className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg"
                    >
                      <div className="flex items-center space-x-3">
                        <FileText className="w-5 h-5 text-slate-500 dark:text-gray-400" />
                        <div>
                          <p className="font-medium text-slate-900 dark:text-white capitalize">
                            {docKey.replace(/_/g, ' ')}
                          </p>
                          {docData.status && (
                            <p className="text-sm text-slate-500 dark:text-gray-400">
                              Status: {docData.status}
                            </p>
                          )}
                        </div>
                      </div>
                      {docData.file_url && (
                        <a
                          href={docData.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 dark:text-blue-400 hover:underline flex items-center space-x-1"
                        >
                          <span>View</span>
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Subscription Info */}
            {subscription && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="card p-6"
              >
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center space-x-2">
                  <Award className="w-5 h-5" />
                  <span>Subscription</span>
                </h3>
                <div className="space-y-2">
                  <div>
                    <label className="text-sm font-medium text-slate-500 dark:text-gray-400">Plan</label>
                    <p className="mt-1 text-slate-900 dark:text-white font-semibold">
                      {subscription.plan_name || 'Free Plan'}
                    </p>
                  </div>
                  {subscription.status && (
                    <div>
                      <label className="text-sm font-medium text-slate-500 dark:text-gray-400">Status</label>
                      <p className="mt-1 text-slate-900 dark:text-white">{subscription.status}</p>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* Company Profile Details */}
            {company_profile && Object.keys(company_profile).length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="card p-6"
              >
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center space-x-2">
                  <Briefcase className="w-5 h-5" />
                  <span>Company Profile Details</span>
                </h3>

                <div className="space-y-4">
                  {company_profile.detailed_description && (
                    <div>
                      <label className="text-sm font-medium text-slate-500 dark:text-gray-400">Description</label>
                      <p className="mt-1 text-slate-900 dark:text-white text-sm">{company_profile.detailed_description}</p>
                    </div>
                  )}

                  {company_profile.industry_domains && (
                    <div>
                      <label className="text-sm font-medium text-slate-500 dark:text-gray-400">Industry Domains</label>
                      <p className="mt-1 text-slate-900 dark:text-white text-sm">{company_profile.industry_domains}</p>
                    </div>
                  )}

                  {company_profile.key_technologies && (
                    <div>
                      <label className="text-sm font-medium text-slate-500 dark:text-gray-400">Key Technologies</label>
                      <p className="mt-1 text-slate-900 dark:text-white text-sm">{company_profile.key_technologies}</p>
                    </div>
                  )}

                  {company_profile.years_in_operation && (
                    <div>
                      <label className="text-sm font-medium text-slate-500 dark:text-gray-400">Years in Operation</label>
                      <p className="mt-1 text-slate-900 dark:text-white text-sm">{company_profile.years_in_operation}</p>
                    </div>
                  )}

                  {company_profile.linkedin_url && (
                    <div>
                      <label className="text-sm font-medium text-slate-500 dark:text-gray-400 flex items-center space-x-1">
                        <Globe className="w-4 h-4" />
                        <span>LinkedIn</span>
                      </label>
                      <a
                        href={company_profile.linkedin_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-1 text-primary-600 dark:text-blue-400 hover:underline flex items-center space-x-1 text-sm"
                      >
                        <span className="truncate">{company_profile.linkedin_url}</span>
                        <ExternalLink className="w-3 h-3 flex-shrink-0" />
                      </a>
                    </div>
                  )}

                  {company_profile.brand_assets_url && (
                    <div>
                      <label className="text-sm font-medium text-slate-500 dark:text-gray-400">Brand Assets</label>
                      <a
                        href={company_profile.brand_assets_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-1 text-primary-600 dark:text-blue-400 hover:underline flex items-center space-x-1 text-sm"
                      >
                        <span>View Brand Assets</span>
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

          </div>
        </div>
      </div>
    </div>
  )
}

export default CompanyProfileView

